import React, { useState, useCallback, useEffect, useRef } from "react";
import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import {
  Button,
  Checkbox,
  DialogActions,
  DialogTitle,
  Flex,
  Modal,
  Text
} from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { GetWSMgr } from "@/Utils/StateLib";
import {
  enumChartTemplateSet,
  enumBoxStyleSet,
  kBoxStyleRule,
  kPositionBoxStyleRule
} from "@coreapp/constants";
import { QChartTemplate } from "@coreapp/CoreApp/QChartTemplate";
import { QBoxLayout } from "@coreapp/CoreApp/QBoxLayout";
import { SaveBoxStyles } from "@/Utils/Promises";
import { useToast, TOAST_STATUS } from "@/providers/ToastProvider";
import { resolvePastePayload } from "./resolvePastePayload";

type ParsedItem<T> = {
  data: T;
  json: any;
  checked: boolean;
};

type PasteViewsModalProps = {
  show: boolean;
  onClose: () => void;
};

export const PasteViewsModal: React.FC<PasteViewsModalProps> = ({ show, onClose }) => {
  const [parsedBoxStyles, setParsedBoxStyles] = useState<ParsedItem<QBoxLayout>[]>([]);
  const [parsedTemplates, setParsedTemplates] = useState<ParsedItem<QChartTemplate>[]>([]);
  const [applying, setApplying] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { addToast }: any = useToast();
  const parseClipboardContent = useCallback(
    (content: string) => {
      try {
        const data = resolvePastePayload(content);

        const templates: ParsedItem<QChartTemplate>[] = data.templates.map((json: any) => {
          const template = new QChartTemplate();
          template.InitTemplate();
          template.LoadFromJSON(json);
          return { data: template, json, checked: true };
        });

        const boxstyles: ParsedItem<QBoxLayout>[] = data.boxstyles.map((json: any) => {
          const layout = new QBoxLayout();
          layout.LoadFromJSON(json);
          layout.FinishLoadBoxStyle();
          return { data: layout, json, checked: true };
        });

        setParsedTemplates(templates);
        setParsedBoxStyles(boxstyles);
      } catch (e) {
        addToast?.({
          message: appstr("Failed to paste views. Please copy views and box styles and try again."),
          status: TOAST_STATUS.ERROR
        });
        setParsedTemplates([]);
        setParsedBoxStyles([]);
      }
    },
    [addToast]
  );

  useEffect(() => {
    if (!show) {
      setParsedTemplates([]);
      setParsedBoxStyles([]);
      return;
    }

    // Ensure keyboard paste shortcuts target this container immediately on open.
    const focusId = requestAnimationFrame(() => {
      containerRef.current?.focus();
    });

    return () => cancelAnimationFrame(focusId);
  }, [show]);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const content = e.clipboardData.getData("text");
      if (content) {
        parseClipboardContent(content);
      }
    },
    [parseClipboardContent]
  );

  const toggleTemplate = useCallback((index: number) => {
    setParsedTemplates(prev => {
      const templates = [...prev];
      templates[index] = { ...templates[index], checked: !templates[index].checked };
      return templates;
    });
  }, []);

  const toggleBoxStyle = useCallback((index: number) => {
    setParsedBoxStyles(prev => {
      const boxstyles = [...prev];
      boxstyles[index] = { ...boxstyles[index], checked: !boxstyles[index].checked };
      return boxstyles;
    });
  }, []);

  const handleApply = useCallback(async () => {
    if (applying) return;
    if (!parsedTemplates.length && !parsedBoxStyles.length) return;

    const wsMgr = GetWSMgr();
    if (!wsMgr) return;

    setApplying(true);

    // Hoisted so the catch block can roll back any manager mutations made
    // before a save failed, leaving the in-memory managers in their prior state.
    let ctMgr: ReturnType<typeof wsMgr.GetChartTemplatesMgr> | null = null;
    let bsMgr: ReturnType<typeof wsMgr.GetBoxStylesMgr> | null = null;
    const importedTemplates: QChartTemplate[] = [];
    const importedBoxStyles: QBoxLayout[] = [];

    try {
      ctMgr = wsMgr.GetChartTemplatesMgr();
      bsMgr = wsMgr.GetBoxStylesMgr();

      if (!ctMgr || !bsMgr) {
        throw new Error("Unable to access templates or box styles manager");
      }

      bsMgr.SwitchBoxStyles(enumBoxStyleSet.sharedboxstyles);
      ctMgr.SwitchTemplates(enumChartTemplateSet.sharedtemplates);

      // Build old ID → new ID mapping for box styles
      const boxStyleIdMap = new Map<string, number>();

      // Import checked box styles first
      const checkedBoxStyles = parsedBoxStyles.filter(b => b.checked);
      for (const item of checkedBoxStyles) {
        const oldId = item.data.GetBoxStyleID();
        const newLayout = bsMgr.AddLayout(item.data, item.data.GetBoxStyleName());
        if (newLayout) {
          importedBoxStyles.push(newLayout);
        }
        const newId = newLayout?.GetBoxStyleID();
        if (oldId != null && newId != null) {
          boxStyleIdMap.set(String(oldId), newId);
        }
      }

      // Import checked templates. This is a two-pass process because a view can
      // link to another view by template ID (the kInheritedRule's fInheritTemplate),
      // and the linked view may be assigned its new ID after the view that links to
      // it. Pass 1 assigns new IDs and builds the old → new ID map; pass 2 remaps
      // both box-style and linked-view references once the full map is known.
      const checkedTemplates = parsedTemplates.filter(t => t.checked);

      // Pass 1: clone, assign new unique IDs, register, and record old → new mapping.
      const templateIdMap = new Map<string, string>();
      for (const item of checkedTemplates) {
        const template = item.data.Clone();
        const oldId = template.GetTemplateID();

        const newId = ctMgr.GetUniqueID();
        template.SetTemplateID(newId);
        template.SetTemplateName(ctMgr.GetUniqueName(template.GetTemplateName()));
        template.SetTemplateLibrary("shared");
        template.SetDirty(true);

        // Register immediately so the next GetUniqueID()/GetUniqueName() account for it.
        ctMgr.ReplaceTemplate(null, template);

        if (oldId != null) {
          templateIdMap.set(String(oldId), String(newId));
        }
        importedTemplates.push(template);
      }

      // Pass 2: remap box-style IDs and linked-view references in every rule.
      for (const template of importedTemplates) {
        const numRules = template.GetNumRules();
        for (let i = 0; i < numRules; i++) {
          const rule = template.GetIndexRule(i);

          const empStyleId = rule.GetBoxStyleID(kBoxStyleRule);
          if (empStyleId != null && boxStyleIdMap.has(String(empStyleId))) {
            rule.SetBoxStyleID(boxStyleIdMap.get(String(empStyleId))!, kBoxStyleRule);
          }

          const posStyleId = rule.GetBoxStyleID(kPositionBoxStyleRule);
          if (posStyleId != null && boxStyleIdMap.has(String(posStyleId))) {
            rule.SetBoxStyleID(boxStyleIdMap.get(String(posStyleId))!, kPositionBoxStyleRule);
          }

          // Repoint a linked view to its newly-assigned ID. If the linked view was
          // not part of this paste, leave the reference untouched.
          const inheritId = rule.GetInheritTemplate();
          if (inheritId != null && templateIdMap.has(String(inheritId))) {
            rule.SetInheritTemplate(templateIdMap.get(String(inheritId))!);
          }
        }
      }

      // Save box styles (promise-based)
      if (checkedBoxStyles.length > 0) {
        await SaveBoxStyles();
      }

      // Save templates (RFC-based)
      if (checkedTemplates.length > 0) {
        wsMgr.SaveChartTemplates();
      }

      ctMgr.UpdateContext?.();

      addToast?.({
        message: appstr("Views and box styles imported successfully."),
        status: TOAST_STATUS.SUCCESS
      });

      onClose();
    } catch (e) {
      // A save failed after we had already registered templates / added box
      // styles in memory. Undo those mutations so the managers return to their
      // prior state and a retry doesn't accumulate duplicates.
      for (const template of importedTemplates) {
        ctMgr?.RemoveTemplate(template);
      }
      for (const layout of importedBoxStyles) {
        bsMgr?.RemoveLayout(layout);
      }

      addToast?.({
        message: appstr("Failed to import views. Please try again."),
        status: TOAST_STATUS.ERROR
      });
    } finally {
      setApplying(false);
    }
  }, [parsedTemplates, parsedBoxStyles, addToast, applying, onClose]);

  const hasCheckedItems =
    (parsedTemplates && parsedTemplates.some(t => t.checked)) ||
    (parsedBoxStyles && parsedBoxStyles.some(b => b.checked));

  return (
    <CentralizedModalContainer show={show}>
      <Modal
        id="paste-views-modal"
        show={show}
        onClose={onClose}
        minWidth="large"
        maxWidth="large"
        withShadow={true}
        centerScreen={true}
        data-testid="paste-views-modal"
      >
        <DialogTitle onClose={onClose} divider>
          {appstr("Paste views and box styles from clipboard")}
        </DialogTitle>
        <div
          ref={containerRef}
          tabIndex={0}
          autoFocus
          onPaste={handlePaste}
          className="oc-paste-views-modal-content"
        >
          <Text size="small">
            {appstr(
              "Use the ctrl/cmd V shortcut key to paste your views and box styles from your clipboard."
            )}
          </Text>
          <Flex gap="24px">
            <Flex direction="column" flex="1" gap="16px">
              <Text modifier="medium">{appstr("Views")}</Text>

              {parsedTemplates.length === 0 ? (
                <Text size="small">{appstr("Paste from your clipboard.")}</Text>
              ) : (
                <Flex direction="column" gap="16px" padding="8px">
                  {parsedTemplates.map((item, idx) => (
                    <Checkbox
                      id={`paste-views-view-row-${item.data.GetTemplateID() || idx}-checkbox`}
                      key={item.data.GetTemplateID() || idx}
                      checked={item.checked}
                      onToggle={() => toggleTemplate(idx)}
                      data-testid={`paste-views-view-row-${item.data.GetTemplateID() || idx}-checkbox`}
                      label={item.data.GetTemplateName()}
                    />
                  ))}
                </Flex>
              )}
            </Flex>

            <Flex direction="column" flex="1" gap="16px">
              <Text modifier="medium">{appstr("Box Styles")}</Text>

              {parsedBoxStyles.length === 0 ? (
                <Text size="small">{appstr("Paste from your clipboard.")}</Text>
              ) : (
                <Flex direction="column" gap="16px" padding="8px">
                  {parsedBoxStyles.map((item, idx) => (
                    <Checkbox
                      id={`paste-views-boxstyle-row-${item.data.GetBoxStyleID() || idx}-checkbox`}
                      key={item.data.GetBoxStyleID() || idx}
                      checked={item.checked}
                      onToggle={() => toggleBoxStyle(idx)}
                      data-testid={`paste-views-boxstyle-row-${item.data.GetBoxStyleID() || idx}-checkbox`}
                      label={item.data.GetBoxStyleName()}
                    />
                  ))}
                </Flex>
              )}
            </Flex>
          </Flex>
        </div>
        <DialogActions divider>
          <Button
            modifier="secondary"
            onClick={onClose}
            disabled={applying}
            data-testid="paste-views-action-cancel"
          >
            {appstr("Cancel")}
          </Button>
          <Button
            modifier="primary"
            onClick={handleApply}
            disabled={!hasCheckedItems || applying}
            loading={applying}
            data-testid="paste-views-action-apply"
          >
            {appstr("Import views")}
          </Button>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};

export default PasteViewsModal;
